import React, { useEffect, useState, useMemo } from "react";
import { api, exportCSV, money, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus as PlusIcon, DownloadSimple as DownloadSimpleIcon, MagnifyingGlass as MagnifyingGlassIcon, PencilSimple as PencilSimpleIcon, Trash as TrashIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

export function StatusBadge({ value }) {
  const map = {
    full: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
    empty: "bg-slate-400/15 text-slate-500",
    in_transit: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
    maintenance: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
    damaged: "bg-destructive/15 text-destructive",
    lost: "bg-destructive/15 text-destructive",
    active: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
    inactive: "bg-slate-400/15 text-slate-500",
    delivered: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
    scheduled: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
    pending: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
    failed: "bg-destructive/15 text-destructive",
    ok: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
    due: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
    overdue: "bg-destructive/15 text-destructive",
  };
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium capitalize ${map[value] || "bg-secondary text-secondary-foreground"}`}>
      {String(value || "-").replace(/_/g, " ")}
    </span>
  );
}

function FieldInput({ field, value, onChange }) {
  if (field.type === "select") {
    return (
      <Select value={value ?? ""} onValueChange={(v) => onChange(field.key, v)}>
        <SelectTrigger data-testid={`field-${field.key}`}><SelectValue placeholder={`Select ${field.label}`} /></SelectTrigger>
        <SelectContent>
          {field.options.map((o) => (
            <SelectItem key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }
  return (
    <Input
      data-testid={`field-${field.key}`}
      type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"}
      value={value ?? ""}
      onChange={(e) => onChange(field.key, field.type === "number" ? e.target.value : e.target.value)}
    />
  );
}

export default function CrudModule({ title, subtitle, endpoint, columns, fields, testid, headerImage }) {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get(`/${endpoint}`).then((r) => setRows(r.data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [endpoint]);

  const filtered = useMemo(() => {
    if (!q) return rows;
    const s = q.toLowerCase();
    return rows.filter((r) => Object.values(r).some((v) => String(v).toLowerCase().includes(s)));
  }, [rows, q]);

  const openNew = () => { setEditing(null); setForm({}); setOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm({ ...row }); setOpen(true); };

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const payload = { ...form };
    fields.forEach((f) => { if (f.type === "number" && payload[f.key] !== undefined && payload[f.key] !== "") payload[f.key] = Number(payload[f.key]); });
    try {
      if (editing) await api.put(`/${endpoint}/${editing.id}`, payload);
      else await api.post(`/${endpoint}`, payload);
      toast.success(`${title} ${editing ? "updated" : "added"}`);
      setOpen(false); load();
    } catch (e) { toast.error("Save failed"); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete this ${title.toLowerCase()} record?`)) return;
    await api.delete(`/${endpoint}/${row.id}`);
    toast.success("Deleted"); load();
  };

  return (
    <div className="space-y-5 animate-fade-up">
      {headerImage && (
        <div className="relative h-32 rounded-lg overflow-hidden">
          <img src={headerImage} alt={title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary/80 to-primary/30 flex items-end p-5">
            <div>
              <h1 className="font-head text-2xl font-bold text-white">{title}</h1>
              <p className="text-white/80 text-sm">{subtitle}</p>
            </div>
          </div>
        </div>
      )}
      {!headerImage && (
        <div>
          <h1 className="font-head text-2xl font-bold">{title}</h1>
          {subtitle && <p className="text-muted-foreground text-sm">{subtitle}</p>}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlassIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid={`${testid}-search`} placeholder="Search..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
        <Button variant="outline" data-testid={`${testid}-export`} onClick={() => exportCSV(`${endpoint}.csv`, filtered)}>
          <DownloadSimpleIcon size={16} className="mr-1.5" /> Export
        </Button>
        <Button data-testid={`${testid}-add`} onClick={openNew}>
          <PlusIcon size={16} className="mr-1.5" /> Add {title.replace(/s$/, "")}
        </Button>
      </div>

      <div className="border rounded-lg bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50">
                {columns.map((c) => <TableHead key={c.key} className={c.align === "right" ? "text-right" : ""}>{c.label}</TableHead>)}
                <TableHead className="text-right w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={columns.length + 1} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>}
              {!loading && filtered.length === 0 && <TableRow><TableCell colSpan={columns.length + 1} className="text-center py-8 text-muted-foreground">No records found</TableCell></TableRow>}
              {filtered.map((row) => (
                <TableRow key={row.id} data-testid={`${testid}-row`} className="hover:bg-secondary/40">
                  {columns.map((c) => (
                    <TableCell key={c.key} className={`py-2 ${c.align === "right" ? "text-right" : ""} ${c.mono ? "font-mono text-xs" : ""}`}>
                      {c.render ? c.render(row[c.key], row) : (row[c.key] ?? "-")}
                    </TableCell>
                  ))}
                  <TableCell className="text-right py-2">
                    <button onClick={() => openEdit(row)} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-primary" data-testid={`${testid}-edit`}><PencilSimpleIcon size={16} /></button>
                    <button onClick={() => remove(row)} className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive" data-testid={`${testid}-delete`}><TrashIcon size={16} /></button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle className="font-head">{editing ? "Edit" : "Add"} {title.replace(/s$/, "")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            {fields.map((f) => (
              <div key={f.key} className={f.full ? "sm:col-span-2" : ""}>
                <Label className="text-xs text-muted-foreground mb-1.5 block">{f.label}</Label>
                <FieldInput field={f} value={form[f.key]} onChange={onChange} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid={`${testid}-save`}>{editing ? "Save Changes" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
